import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, qs } from '@/lib/api';
import { getPages } from './pagedList';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime, humanize } from '@/lib/utils';
import { DataTable, Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Checkbox, Field, Input, Select } from '@/components/ui/primitives';
import { statusLabel } from '@/components/ui/badge';
import { useToast } from '@/components/ui/toast';

interface UnmatchedRow {
  id: string;
  accessionNo: string | null;
  testCode: string;
  value: string;
  unit: string | null;
  flag: string | null;
  reason: string;
  receivedAt: string;
  instrument: { id: string; code: string; name: string } | null;
  rematchable?: boolean;
  needsVerifier?: boolean;
}

interface RematchPage {
  checked: number;
  matched: number;
  reparked: number;
  waiting: number;
  failed: { id: string; reason: string }[];
  nextCursor: string | null;
}

const MAX_REMATCH_PAGES = 20;

async function rematchAll(): Promise<Omit<RematchPage, 'nextCursor'>> {
  const total = { checked: 0, matched: 0, reparked: 0, waiting: 0, failed: [] as RematchPage['failed'] };
  let cursor: string | undefined;
  for (let page = 0; page < MAX_REMATCH_PAGES; page += 1) {
    const result = await post<RematchPage>('/lab/instruments/unmatched/rematch', { limit: 100, ...(cursor ? { cursor } : {}) });
    total.checked += result.checked;
    total.matched += result.matched;
    total.reparked += result.reparked;
    total.waiting += result.waiting;
    total.failed.push(...result.failed);
    if (!result.nextCursor) break;
    cursor = result.nextCursor;
  }
  return total;
}

interface WorklistItem {
  id: string;
  accessionNo: string | null;
  status: string;
  test: { id: string; code: string; name: string };
  patient: { fullName: string; mrn: string };
}

export function UnmatchedResultsDialog({ canResolve, onClose }: { canResolve: boolean; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [resolving, setResolving] = useState<UnmatchedRow | null>(null);
  const rows = useQuery({
    queryKey: ['lab-unmatched'],
    queryFn: () => getPages<UnmatchedRow>('/lab/instruments/unmatched', { status: 'pending', limit: 100 }, 3),
  });
  const mayVerify = can('lab.result.verify');
  const rematch = useMutation({
    mutationFn: rematchAll,
    onSuccess: (total) => {
      const parts = [`${total.matched} matched`, `${total.waiting} still waiting`];
      if (total.reparked) parts.push(`${total.reparked} need a person`);
      if (total.failed.length) parts.push(`${total.failed.length} refused`);
      toast.success(`Re-checked ${total.checked} parked result${total.checked === 1 ? '' : 's'}`, parts.join(' · '));
      void queryClient.invalidateQueries({ queryKey: ['lab-unmatched'] });
      void queryClient.invalidateQueries({ queryKey: ['lab-instruments'] });
      void queryClient.invalidateQueries({ queryKey: ['lab-worklist'] });
    },
    onError: (caught) => toast.error(tr('lab', 'could_not_re_check_the_parked_results'), describeError(caught)),
  });
  const anyRematchable = (rows.data ?? []).some((row) => row.rematchable);

  return (
    <Dialog open onClose={onClose} wide title={tr('lab', 'unmatched_analyser_results')} description={tr('lab', 'frames_whose_accession_or_test_code_matched')}>
      {canResolve && anyRematchable && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => rematch.mutate()} loading={rematch.isPending}>
            {tr('lab', 're_match_parked_results')}
          </Button>
          <span className="text-xs text-muted-foreground">
            {tr('lab', 'tries_again_the_results_whose_specimen_was_not')}
          </span>
        </div>
      )}
      <DataTable
        rows={rows.data}
        loading={rows.isLoading} refreshing={rows.isFetching}
        error={rows.error}
        rowKey={(row) => row.id}
        dense
        empty={tr('lab', 'nothing_is_waiting_every_analyser_result_found')}
        columns={[
          { header: tr('lab', 'received'), cell: (row) => <span className="text-xs">{dateTime(row.receivedAt)}</span> },
          { header: tr('lab', 'instrument'), cell: (row) => <span className="text-xs">{row.instrument?.name ?? '—'}</span> },
          { header: tr('lab', 'accession'), cell: (row) => <span className="font-mono text-xs">{row.accessionNo ?? '—'}</span> },
          { header: tr('lab', 'code'), cell: (row) => <span className="font-mono text-xs">{row.testCode}</span> },
          {
            header: tr('lab', 'value'),
            cell: (row) => (
              <span>
                {row.value} {row.unit ?? ''}
                {row.flag && row.flag !== 'normal' && <Badge variant="warning" className="ms-1">{humanize(row.flag)}</Badge>}
              </span>
            ),
          },
          {
            header: tr('lab', 'why'),
            cell: (row) => (
              <span className="text-xs text-muted-foreground">
                {row.reason}
                {row.needsVerifier && (
                  <Badge variant="destructive" className="ms-1">
                    {tr('lab', 'verifier')}
                  </Badge>
                )}
              </span>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              canResolve && (!row.needsVerifier || mayVerify) ? (
                <Button size="sm" variant="outline" onClick={() => setResolving(row)}>
                  {tr('lab', 'resolve')}
                </Button>
              ) : null,
          },
        ]}
      />
      {resolving && <ResolveDialog row={resolving} onClose={() => setResolving(null)} />}
    </Dialog>
  );
}

function ResolveDialog({ row, onClose }: { row: UnmatchedRow; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [accession, setAccession] = useState(row.accessionNo ?? '');
  const [orderItemId, setOrderItemId] = useState('');
  const [rememberCode, setRememberCode] = useState(true);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const candidates = useQuery({
    queryKey: ['lab-worklist', 'accession', accession],
    queryFn: () => get<{ data: WorklistItem[] }>(`/lab/worklist${qs({ accessionNo: accession, statuses: 'collected,received,in_process,resulted', limit: 50 })}`),
    enabled: accession.trim().length >= 3,
  });

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['lab-unmatched'] });
    void queryClient.invalidateQueries({ queryKey: ['lab-instruments'] });
    void queryClient.invalidateQueries({ queryKey: ['lab-worklist'] });
    onClose();
  };
  const match = useMutation({
    mutationFn: () => post(`/lab/instruments/unmatched/${row.id}/resolve`, { action: 'match', orderItemId, rememberCode, note: note || undefined }),
    onSuccess: () => {
      toast.success(tr('lab', 'result_matched_and_entered_on_the_bench'));
      done();
    },
    onError: (caught) => setError(describeError(caught)),
  });
  const discard = useMutation({
    mutationFn: () => post(`/lab/instruments/unmatched/${row.id}/resolve`, { action: 'discard', note }),
    onSuccess: () => {
      toast.success(tr('lab', 'result_discarded'));
      done();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const items = candidates.data?.data ?? [];

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${row.testCode} = ${row.value} ${row.unit ?? ''}`}
      description={row.reason}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('lab', 'cancel')}
          </Button>
          <Button variant="outline" onClick={() => { setError(null); discard.mutate(); }} loading={discard.isPending} disabled={note.trim().length === 0}>
            {tr('lab', 'discard')}
          </Button>
          <Button onClick={() => { setError(null); match.mutate(); }} loading={match.isPending} disabled={!orderItemId || (row.needsVerifier === true && note.trim().length < 5)}>
            {tr('lab', 'match_to_this_specimen')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert variant="destructive">{error}</Alert>}
        <Field label={tr('lab', 'accession_number')} hint={tr('lab', 'type_or_scan_the_tubes_accession_to_find_the')}>
          <Input value={accession} onChange={(e) => { setAccession(e.target.value.toUpperCase()); setOrderItemId(''); }} placeholder={tr('lab', 'e_g_acc_2026_000123')} />
        </Field>
        <Field label={tr('lab', 'specimen_and_test')} hint={items.length === 0 && accession.length >= 3 && !candidates.isFetching ? tr('lab', 'no_collected_specimen_carries_that_accession') : undefined}>
          <Select value={orderItemId} onChange={(e) => setOrderItemId(e.target.value)} disabled={items.length === 0}>
            <option value="">{items.length ? tr('lab', 'pick_the_test_this_value_belongs_to') : '—'}</option>
            {items.map((item) => (
              <option key={item.id} value={item.id}>
                {item.accessionNo} · {item.test.name} ({item.test.code}) · {item.patient.fullName} {item.patient.mrn} · {statusLabel(item.status)}
              </option>
            ))}
          </Select>
        </Field>
        {row.instrument && (
          <Checkbox
            checked={rememberCode}
            onCheckedChange={setRememberCode}
            label={
              <>
                {tr('lab', 'remember')} {row.instrument.name} {tr('lab', 'sends')} <span className="font-mono">{row.testCode}</span> {tr('lab', 'for_this_test')}
              </>
            }
          />
        )}
        {row.needsVerifier && (
          <Alert variant="warning">
            {tr('lab', 'the_analyser_refused_this_value_for_a_safety')}
          </Alert>
        )}
        <Field label={tr('lab', 'note_2')} hint={row.needsVerifier ? tr('lab', 'required_why_this_value_is_safe_to_attach') : tr('lab', 'required_to_discard_optional_when_matching')}>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={tr('lab', 'why_this_is_being_matched_or_thrown_away')} />
        </Field>
      </div>
    </Dialog>
  );
}
